// Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard } from '../services/api';
import { removeToken, getToken, isAuthenticated } from '../utils/auth';

function Dashboard({ onLogout }) {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtre a zoradenie
  const [statusFilters, setStatusFilters] = useState({
    active: true,
    completed: true,
    notStarted: true
  });
  const [sortBy, setSortBy] = useState('last_lesson_desc');

  // Ref na zabránenie dvojitému volaniu v StrictMode
  const effectRan = useRef(false);

  // Získať meno lektora z sessionStorage
  const lectorName = sessionStorage.getItem('lectorName') || 'Lektor';

  useEffect(() => {
    if (effectRan.current === true) {
      return;
    }
    effectRan.current = true;

    // Jednoduchá kontrola autentifikácie
    if (!isAuthenticated()) {
      console.log('User not authenticated, redirecting to login');
      handleLogout();
      return;
    }

    const token = getToken();
    if (!token) {
      window.location.href = '/login';
      return;
    }

    // Cache logika
    const cachedPackages = sessionStorage.getItem('cachedPackages');
    const cacheTimestamp = sessionStorage.getItem('cacheTimestamp');
    const isCacheValid = cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < 5 * 60 * 1000;

    if (cachedPackages && isCacheValid) {
      const packagesData = JSON.parse(cachedPackages);
      setPackages(packagesData);
      setFilteredPackages(packagesData);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const packagesData = await getDashboard();
        sessionStorage.setItem('cachedPackages', JSON.stringify(packagesData));
        sessionStorage.setItem('cacheTimestamp', Date.now().toString());

        setPackages(packagesData);
        setFilteredPackages(packagesData);
      } catch (err) {
        if (err.message.includes('403') || err.message.includes('401')) {
          handleLogout();
        } else {
          setError('Nepodarilo sa načítať dáta: ' + err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      console.log('Dashboard cleanup');
    };
  }, []);

  // Funkcia pre odhlásenie
  const handleLogout = () => {
    removeToken();
    clearAllCache();
    if (onLogout) {
      onLogout();
    }
    window.location.href = '/login';
  };

  // Funkcia na vymazanie cache
  const clearAllCache = () => {
    sessionStorage.removeItem('cachedPackages');
    sessionStorage.removeItem('cacheTimestamp');
    sessionStorage.removeItem('lectorName');
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('packageDetail_') || key.startsWith('lessons_')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  // Filtrovanie a zoradenie (zostáva rovnaké)
  useEffect(() => {
    let result = [...packages];

    // Filtrovanie podľa statusu
    result = result.filter(pkg => {
      const status = pkg.status?.toLowerCase() || '';
      const isActive = status.includes('prebieha') || status.includes('active');
      const isCompleted = status.includes('skončil') || status.includes('completed') || status.includes('ukončen');
      const isNotStarted = status.includes('nezačal') || status.includes('not started') || status.includes('pending');

      return (
        (statusFilters.active && isActive) ||
        (statusFilters.completed && isCompleted) ||
        (statusFilters.notStarted && isNotStarted)
      );
    });

    // Zoradenie
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.nazov_balicka || '').localeCompare(b.nazov_balicka || '');
        case 'remaining_asc':
          return (a.zostava_lekcii || 0) - (b.zostava_lekcii || 0);
        case 'remaining_desc':
          return (b.zostava_lekcii || 0) - (a.zostava_lekcii || 0);
        case 'taught_asc':
          return (a.oducene_lekcie || 0) - (b.oducene_lekcie || 0);
        case 'taught_desc':
          return (b.oducene_lekcie || 0) - (a.oducene_lekcie || 0);
        case 'last_lesson_asc':
          return new Date(a.last_lesson_date || 0) - new Date(b.last_lesson_date || 0);
        case 'last_lesson_desc':
          return new Date(b.last_lesson_date || 0) - new Date(a.last_lesson_date || 0);
        default:
          return 0;
      }
    });

    setFilteredPackages(result);
  }, [packages, statusFilters, sortBy]);

  const handleStatusFilterChange = (filterName) => {
    setStatusFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-badge nezačal';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('prebieha') || statusLower.includes('active')) {
      return 'status-badge prebieha';
    } else if (statusLower.includes('skončil') || statusLower.includes('completed') || statusLower.includes('ukončen')) {
      return 'status-badge skoncil';
    } else if (statusLower.includes('nezačal') || statusLower.includes('not started') || statusLower.includes('pending')) {
      return 'status-badge nezačal';
    }

    return 'status-badge nezačal';
  };

  const getStudentName = (pkg) => {
    return pkg.meno_studenta || pkg.nazov_balicka || 'Bez mena';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Žiadna lekcia';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('sk-SK');
    } catch {
      return dateString;
    }
  };

  if (loading) return <div className="loading">Načítavam balíčky...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="dashboard-container">
      <header className="main-header">
        <div className="user-greeting">
          <h1>Ahoj, {lectorName}!</h1>
          <p>Celkovo balíčkov: <strong>{packages.length}</strong> | Zobrazených: <strong>{filteredPackages.length}</strong></p>
        </div>
        <div className="header-actions">
          {/* PRIDANÉ TLAČIDLO PRE SPRÁVU DOSTUPNOSTI */}
          <Link to="/availability" className="availability-btn">
            Správa dostupnosti
          </Link>
          <button className="logout-btn" onClick={handleLogout}>
            Odhlásiť sa
          </button>
        </div>
      </header>

      {/* Filtre a zoradenie */}
      <div className="filter-panel">
        <div className="status-checkboxes">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={statusFilters.active}
              onChange={() => handleStatusFilterChange('active')}
            />
            <span className="checkmark"></span>
            Prebieha
          </label>
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={statusFilters.completed}
              onChange={() => handleStatusFilterChange('completed')}
            />
            <span className="checkmark"></span>
            Skončil
          </label>
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={statusFilters.notStarted}
              onChange={() => handleStatusFilterChange('notStarted')}
            />
            <span className="checkmark"></span>
            Ešte nezačal
          </label>
        </div>

        <div className="sort-by">
          <label htmlFor="sort-select">Zoradiť podľa</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="last_lesson_desc">Posledná lekcia (od najnovšej)</option>
            <option value="last_lesson_asc">Posledná lekcia (od najstaršej)</option>
            <option value="name">Názov balíčka (A-Z)</option>
            <option value="remaining_asc">Zostáva lekcií (vzostupne)</option>
            <option value="remaining_desc">Zostáva lekcií (zostupne)</option>
            <option value="taught_asc">Odučené lekcie (vzostupne)</option>
            <option value="taught_desc">Odučené lekcie (zostupne)</option>
          </select>
        </div>
      </div>

      {/* Zoznam balíčkov */}
      <main className="packages-grid">
        {filteredPackages.map((pkg) => (
          <article key={pkg.id} className="package-card">
            <div className="card-header">
              <h2>{getStudentName(pkg)}</h2>
              <span className={getStatusClass(pkg.status)}>
                {pkg.status || 'Neznámy stav'}
              </span>
            </div>
            <div className="card-info">
              <p>Posledná lekcia: {formatDate(pkg.last_lesson_date)}</p>
              <p>Odučené: {pkg.oducene_lekcie || 0}/{pkg.kupene_lekcie || 0}</p>
              <p>Zostáva: {(pkg.kupene_lekcie || 0) - (pkg.oducene_lekcie || 0)}</p>
            </div>
            <Link to={`/package/${pkg.id}`} className="view-lessons-btn">
              Zobraziť lekcie
            </Link>
          </article>
        ))}
      </main>

      {filteredPackages.length === 0 && (
        <div className="no-data">
          {packages.length === 0
            ? 'Žiadne balíčky na zobrazenie'
            : 'Žiadne balíčky nezodpovedajú zvoleným filtrom'}
        </div>
      )}
    </div>
  );
}

export default Dashboard;